// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { useCallback, useEffect, useRef, useState } from 'react';

/** One batch-worker server as the fleet page shows it. */
export type FleetWorker = {
  workerId: string;
  host: string;
  version: string | null;
  capabilities: string[];
  currentJobId: string | null;
  lastSeenAt: string;
  online: boolean;
};

/** How many jobs of each kind are waiting for a worker right now. */
export type FleetQueues = {
  courseAnalysisDispatched: number;
  flyoverQueued: number;
  mlQueued: number;
};

/** One job from any of the three kinds, already merged into one timeline. */
export type FleetActivityItem = {
  kind: 'course-analysis' | 'flyover' | 'ml';
  id: string;
  status: string;
  at: string;
  finishedAt: string | null;
  workerId: string | null;
  summary: string | null;
  error: string | null;
};

export type FleetSnapshot = {
  workers: FleetWorker[];
  queues: FleetQueues;
  activity: FleetActivityItem[];
};

export type FleetState =
  | { status: 'loading' }
  | { status: 'forbidden' }
  | { status: 'failed' }
  | ({ status: 'ready'; loadedAt: number } & FleetSnapshot);

type Row = Record<string, unknown>;

const str = (row: Row, ...keys: string[]): string | null => {
  for (const key of keys) {
    const value = row[key];
    if (typeof value === 'string' && value.length > 0) return value;
  }
  return null;
};

const num = (row: Row, ...keys: string[]): number => {
  for (const key of keys) {
    const value = row[key];
    if (typeof value === 'number' && Number.isFinite(value)) return value;
  }
  return 0;
};

const bool = (row: Row, ...keys: string[]): boolean => {
  for (const key of keys) {
    const value = row[key];
    if (typeof value === 'boolean') return value;
  }
  return false;
};

const strArray = (row: Row, ...keys: string[]): string[] => {
  for (const key of keys) {
    const value = row[key];
    if (Array.isArray(value)) return value.filter((v): v is string => typeof v === 'string');
  }
  return [];
};

const isFleetKind = (value: string | null): value is FleetActivityItem['kind'] =>
  value === 'course-analysis' || value === 'flyover' || value === 'ml';

/** Maps the BFF's `/api/geodata/batch/fleet` body — pulled out for direct unit testing. */
export const mapFleetResponse = (body: unknown): FleetSnapshot => {
  const root = (body ?? {}) as Row;

  const workerRows = (root.workers ?? root.Workers) as Row[] | undefined;
  const workers: FleetWorker[] = Array.isArray(workerRows)
    ? workerRows
        .map((row) => ({
          workerId: str(row, 'workerId', 'WorkerId') ?? '',
          host: str(row, 'host', 'Host') ?? '',
          version: str(row, 'version', 'Version'),
          capabilities: strArray(row, 'capabilities', 'Capabilities'),
          currentJobId: str(row, 'currentJobId', 'CurrentJobId'),
          lastSeenAt: str(row, 'lastSeenAt', 'LastSeenAt') ?? '',
          online: bool(row, 'online', 'Online'),
        }))
        .filter((worker) => worker.workerId.length > 0)
    : [];

  const queueRow = (root.queues ?? root.Queues ?? {}) as Row;
  const queues: FleetQueues = {
    courseAnalysisDispatched: num(queueRow, 'courseAnalysisDispatched', 'CourseAnalysisDispatched'),
    flyoverQueued: num(queueRow, 'flyoverQueued', 'FlyoverQueued'),
    mlQueued: num(queueRow, 'mlQueued', 'MlQueued'),
  };

  const activityRows = (root.recentActivity ?? root.RecentActivity) as Row[] | undefined;
  const activity: FleetActivityItem[] = Array.isArray(activityRows)
    ? activityRows
        .map((row) => {
          const kind = str(row, 'kind', 'Kind');
          const id = str(row, 'id', 'Id');
          const at = str(row, 'at', 'At');
          if (!isFleetKind(kind) || !id || !at) return null;
          return {
            kind,
            id,
            status: str(row, 'status', 'Status') ?? '',
            at,
            finishedAt: str(row, 'finishedAt', 'FinishedAt'),
            workerId: str(row, 'workerId', 'WorkerId'),
            summary: str(row, 'summary', 'Summary'),
            error: str(row, 'error', 'Error'),
          };
        })
        .filter((item): item is FleetActivityItem => item !== null)
    : [];

  return { workers, queues, activity };
};

/**
 * Polls the batch-worker fleet page's data. Kept to a plain interval rather than a
 * WebSocket subscription: this is a read-only operational view a developer opens
 * occasionally, not a live control surface, and geodata already answers the same shape to
 * the per-course/per-job panels from a query with no push side to reuse.
 */
export function useBatchFleet(
  apiBaseUrl: string,
  token: string | undefined,
  enabled: boolean,
  pollMs = 15000
): { state: FleetState; refresh: () => void } {
  const [state, setState] = useState<FleetState>({ status: 'loading' });
  const reloadKeyRef = useRef(0);
  const [reloadKey, setReloadKey] = useState(0);

  const refresh = useCallback(() => {
    reloadKeyRef.current += 1;
    setReloadKey(reloadKeyRef.current);
  }, []);

  useEffect(() => {
    if (!enabled || !token) {
      return;
    }

    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const load = async () => {
      try {
        const response = await fetch(`${apiBaseUrl}/api/geodata/batch/fleet`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (cancelled) return;

        if (response.status === 401 || response.status === 403) {
          setState({ status: 'forbidden' });
          return;
        }
        if (!response.ok) {
          setState({ status: 'failed' });
          return;
        }

        const body = await response.json();
        if (cancelled) return;
        setState({ status: 'ready', loadedAt: Date.now(), ...mapFleetResponse(body) });
      } catch {
        if (!cancelled) {
          setState({ status: 'failed' });
        }
      } finally {
        if (!cancelled) {
          timer = setTimeout(load, pollMs);
        }
      }
    };

    void load();

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [apiBaseUrl, token, enabled, pollMs, reloadKey]);

  return { state, refresh };
}
